import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Container, Row, Col, Spinner, Dropdown, ButtonGroup, Button, OverlayTrigger, Tooltip, Form } from 'react-bootstrap'
import { inventoryItemTransferAPI } from '../api/inventoryItemTransferAPI'
import { vendorAPI } from '../api/vendorAPI'
import { highlightText, formatDateOnly, formatQuantity } from '../utils/commonUtils'
import PaginationComponent from '../components/Pagination'
import { createColumnToggleDropdown, handleSearchWithDebounce } from '../utils/tableUtils'
import StockItemInfo from '../components/StockItemInfo'
import { usePermissions } from '../contexts/PermissionContext'
import 'material-icons/iconfont/material-icons.css'
import ResizableTable from '../components/ResizableTable'
import SearchInputWithClear from '../components/SearchInputWithClear'

function InventoryTransfersListScreen() {
    const { hasPermission } = usePermissions()
    const [transfers, setTransfers] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [vendorMap, setVendorMap] = useState({})
    const navigate = useNavigate()
    const [columnVisibility, setColumnVisibility] = useState(() => {
        const savedVisibility = localStorage.getItem('inventoryTransferColumnVisibility');
        return savedVisibility ? JSON.parse(savedVisibility) : {
            transfer_id: true,
            direction: true,
            inventory_id: true,
            image: true,
            gtin: true,
            description: true,
            lot: true,
            bb_date: true,
            quantity_sent: true,
            quantity_received: true,
            source_warehouse: true,
            source_location: true,
            destination_warehouse: true,
            destination_location: true,
            date_started: true,
            date_completed: true,
            user: true,
            status: true,
            notes: true,
            issue_description: true,
            resolution_notes: true,
        };
    });

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('inventoryTransferColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            actions: 'auto',
            direction: 'auto',
            transfer_id: 'auto',
            inventory_id: 'auto',
            image: 'auto',
            gtin: 'auto',
            description: 'auto',
            lot: 'auto',
            bb_date: 'auto',
            quantity_sent: 'auto',
            quantity_received: 'auto',
            source_warehouse: 'auto',
            source_location: 'auto',
            destination_warehouse: 'auto',
            destination_location: 'auto',
            date_started: 'auto',
            date_completed: 'auto',
            user: 'auto',
            status: 'auto',
            notes: 'auto',
            issue_description: 'auto',
            resolution_notes: 'auto',
        };
    });

    const [totalPages, setTotalPages] = useState(0);
    const debounceTimeoutRef = useRef(null);
    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null
    });
    const [displayedDateRange, setDisplayedDateRange] = useState({ startDate: null, endDate: null });
    const [selectedSourceWarehouse, setSelectedSourceWarehouse] = useState('');
    const [selectedDestWarehouse, setSelectedDestWarehouse] = useState('');
    const [selectedSourceLocation, setSelectedSourceLocation] = useState('');
    const [selectedDestLocation, setSelectedDestLocation] = useState('');
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize());
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        localStorage.setItem('inventoryTransferColumnVisibility', JSON.stringify(columnVisibility));
    }, [columnVisibility]);

    useEffect(() => {
        localStorage.setItem('inventoryTransferColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    const columnLabels = {
        transfer_id: 'Transfer ID',
        direction: 'Direction',
        inventory_id: 'Inventory ID',
        image: 'Image',
        gtin: 'GTIN',
        description: 'Description',
        lot: 'LOT',
        bb_date: 'Best Before',
        quantity_sent: 'Quantity Sent',
        quantity_received: 'Quantity Received',
        source_warehouse: 'Source Warehouse',
        source_location: 'Source Location',
        destination_warehouse: 'Destination Warehouse',
        destination_location: 'Destination Location',
        date_started: 'Date Started',
        date_completed: 'Date Completed',
        user: 'User',
        status: 'Status',
        notes: 'Notes',
        issue_description: 'Issue Description',
        resolution_notes: 'Resolution Notes'
    };

    useEffect(() => {
        async function fetchTransfers() {
            try {
                setLoading(true);
                let inTransit = undefined;
                let isCompleted = undefined;
                let isCancelled = undefined;
                let hasIssue = undefined;
                let issueResolved = undefined;

                // Handle multiple selected statuses
                selectedStatus.forEach(status => {
                    switch(status) {
                        case 'in_transit':
                            inTransit = true;
                            break;
                        case 'completed':
                            isCompleted = true;
                            break;
                        case 'cancelled':
                            isCancelled = true;
                            break;
                        case 'has_issue':
                            hasIssue = true;
                            issueResolved = false;
                            break;
                        case 'issue_resolved':
                            hasIssue = true;
                            issueResolved = true;
                            break;
                    }
                });

                const response = await inventoryItemTransferAPI.getAll(
                    currentPage,
                    pageSize,
                    searchTerm,
                    selectedSourceWarehouse,
                    selectedDestWarehouse,
                    selectedSourceLocation,
                    selectedDestLocation,
                    dateRange.startDate?.toISOString().split('T')[0],
                    dateRange.endDate?.toISOString().split('T')[0],
                    inTransit,
                    isCompleted,
                    isCancelled,
                    hasIssue,
                    issueResolved
                );
                
                if (!Array.isArray(response.results)) {
                    throw new Error('Expected an array of transfers');
                }
                setTransfers(response.results);
                setTotalPages(response.count);
            } catch (error) {
                console.error('Error fetching transfers:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchTransfers();
    }, [currentPage, searchTerm, selectedSourceWarehouse, selectedDestWarehouse, 
        selectedSourceLocation, selectedDestLocation, selectedStatus, pageSize, dateRange, refreshTrigger]);

    useEffect(() => {
        async function fetchVendors() {
            try {
                const vendors = await vendorAPI.getAll()
                const mapping = vendors.reduce((acc, vendor) => {
                    acc[vendor._id] = vendor.name
                    return acc
                }, {})
                setVendorMap(mapping)
            } catch (error) {
                console.error('Error fetching vendors:', error)
            }
        }
        fetchVendors()
    }, [])

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber)
    }

    const toggleColumnVisibility = (column) => {
        setColumnVisibility(prevState => ({
            ...prevState,
            [column]: !prevState[column]
        }));
    };

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const handleStatusFilterChange = (status) => {
        if (status === '') {
            // Clear all selections
            setSelectedStatus([]);
        } else {
            setSelectedStatus(prev => {
                const newSelection = [...prev];
                const index = newSelection.indexOf(status);
                if (index > -1) {
                    // Remove if already selected
                    newSelection.splice(index, 1);
                } else {
                    // Add if not selected
                    newSelection.push(status);
                }
                return newSelection;
            });
        }
        setCurrentPage(1);
    };

    const getStatusBadgeVariant = (transfer) => {
        if (transfer.is_cancelled) return 'danger';
        if (transfer.has_transfer_issue) {
            return transfer.is_transfer_issue_resolved ? 'info' : 'warning';
        }
        if (transfer.date_completed) return 'success';
        return 'primary';
    };

    const getStatusText = (transfer) => {
        if (transfer.is_cancelled) return 'Cancelled';
        if (transfer.date_completed) {
            if (transfer.has_transfer_issue) {
                return transfer.is_transfer_issue_resolved 
                    ? 'Completed. Issue Resolved' 
                    : 'Completed. Has Issue';
            }
            return 'Completed';
        }
        if (transfer.has_transfer_issue) {
            return transfer.is_transfer_issue_resolved ? 'Issue Resolved' : 'Has Issue';
        }
        return 'In Transit';
    };

    const getWarehouseAbbreviation = (warehouseName) => {
        if (!warehouseName) return '???';
        // Remove any spaces and take first 3 letters
        return warehouseName.replace(/\s+/g, '').substring(0, 3).toUpperCase();
    };

    const handleTransferAction = async (transfer, action) => {
        try {
            if (action === 'cancel') {
                if (!window.confirm('Are you sure you want to cancel this transfer?')) {
                    return;
                }
                await inventoryItemTransferAPI.cancelTransfer(transfer._id, '');
            } else if (action === 'receive') {
                navigate(`/transfers/${transfer._id}/receive`);
                return;
            }
            
            // Reset to first page and trigger a refresh
            setCurrentPage(1);
            setRefreshTrigger(prev => prev + 1);
        } catch (error) {
            console.error(`Error ${action}ing transfer:`, error);
        }
    };

    return (
        <Container fluid className="px-4 py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Inventory Transfers</h1>
                </Col>
                <Col>
                    {createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search transfers..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <div className="table-responsive">
                    <ResizableTable
                        tableId="inventory_transfers_table"
                        columns={[
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => !row.date_completed && !row.is_cancelled && (
                                    <ButtonGroup size="sm" className="gap-1">
                                        {hasPermission('inventory.receive') && (
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => handleTransferAction(row, 'receive')}
                                            >
                                                Receive
                                            </Button>
                                        )}
                                        {hasPermission('inventory.move') && (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleTransferAction(row, 'cancel')}
                                            >
                                                Cancel
                                            </Button>
                                        )}
                                    </ButtonGroup>
                                )
                            },
                            {
                                id: 'direction',
                                header: 'Direction',
                                cell: (row) => (
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="fw-bold">{getWarehouseAbbreviation(row.source_warehouse?.name)}</span>
                                        <i className="material-icons" style={{ fontSize: '1.2rem' }}>arrow_forward</i>
                                        <span className="fw-bold">{getWarehouseAbbreviation(row.destination_warehouse?.name)}</span>
                                    </div>
                                )
                            },
                            ...(columnVisibility.transfer_id ? [{
                                id: 'transfer_id',
                                header: 'Transfer ID',
                                cell: (row) => highlightText(row._id, searchTerm)
                            }] : []),
                            ...(columnVisibility.inventory_id ? [{
                                id: 'inventory_id',
                                header: 'Inventory ID',
                                cell: (row) => row.inventory_item?._id ? highlightText(row.inventory_item._id, searchTerm) : ''
                            }] : []),
                            ...(columnVisibility.image ? [{
                                id: 'image',
                                header: 'Image',
                                cell: (row) => row.stock_item?.image ? (
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip className="stock-item-tooltip">
                                                <StockItemInfo  stockItem={row.stock_item} />
                                            </Tooltip>
                                        }
                                    >
                                        <img 
                                            src={row.stock_item.image} 
                                            alt="Stock Item Thumbnail"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </OverlayTrigger>
                                ) : null
                            }] : []),
                            ...(columnVisibility.gtin ? [{
                                id: 'gtin',
                                header: 'GTIN',
                                cell: (row) => highlightText(row.stock_item?.gtin || '', searchTerm)
                            }] : []),
                            ...(columnVisibility.description ? [{
                                id: 'description',
                                header: 'Description',
                                cell: (row) => highlightText(row.stock_item?.description || '', searchTerm)
                            }] : []),
                            ...(columnVisibility.lot ? [{
                                id: 'lot',
                                header: 'LOT',
                                cell: (row) => highlightText(row.inventory_item?.lot || '', searchTerm)
                            }] : []),
                            ...(columnVisibility.bb_date ? [{
                                id: 'bb_date',
                                header: 'Best Before',
                                cell: (row) => formatDateOnly(row.inventory_item?.bb_date)
                            }] : []),
                            ...(columnVisibility.quantity_sent ? [{
                                id: 'quantity_sent',
                                header: 'Quantity Sent',
                                cell: (row) => formatQuantity(row.quantity_sent)
                            }] : []),
                            ...(columnVisibility.quantity_received ? [{
                                id: 'quantity_received',
                                header: 'Quantity Received',
                                cell: (row) => formatQuantity(row.quantity_received)
                            }] : []),
                            ...(columnVisibility.source_warehouse ? [{
                                id: 'source_warehouse',
                                header: 'Source Warehouse',
                                cell: (row) => row.source_warehouse?.name
                            }] : []),
                            ...(columnVisibility.source_location ? [{
                                id: 'source_location',
                                header: 'Source Location',
                                cell: (row) => row.source_location?.name
                            }] : []),
                            ...(columnVisibility.destination_warehouse ? [{
                                id: 'destination_warehouse',
                                header: 'Destination Warehouse',
                                cell: (row) => row.destination_warehouse?.name
                            }] : []),
                            ...(columnVisibility.destination_location ? [{
                                id: 'destination_location',
                                header: 'Destination Location',
                                cell: (row) => row.destination_location?.name
                            }] : []),
                            ...(columnVisibility.date_started ? [{
                                id: 'date_started',
                                header: 'Date Started',
                                cell: (row) => new Date(row.date_started).toLocaleString()
                            }] : []),
                            ...(columnVisibility.date_completed ? [{
                                id: 'date_completed',
                                header: 'Date Completed',
                                cell: (row) => row.date_completed ? new Date(row.date_completed).toLocaleString() : ''
                            }] : []),
                            ...(columnVisibility.user ? [{
                                id: 'user',
                                header: 'User',
                                cell: (row) => row.user_name
                            }] : []),
                            ...(columnVisibility.status ? [{
                                id: 'status',
                                header: () => (
                                    <Dropdown>
                                        <Dropdown.Toggle 
                                            variant="light" 
                                            id="status-filter-dropdown"
                                        >
                                            Status Filter
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {[
                                                { key: 'in_transit', label: 'In Transit' },
                                                { key: 'completed', label: 'Completed' },
                                                { key: 'cancelled', label: 'Cancelled' },
                                                { key: 'has_issue', label: 'Has Issue' },
                                                { key: 'issue_resolved', label: 'Issue Resolved' }
                                            ].map(status => (
                                                <Dropdown.Item 
                                                    key={status.key} 
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleStatusFilterChange(status.key);
                                                    }}
                                                >
                                                    <Form.Check 
                                                        type="checkbox"
                                                        label={status.label}
                                                        checked={selectedStatus.includes(status.key)}
                                                        onChange={() => handleStatusFilterChange(status.key)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </Dropdown.Item>
                                            ))}
                                            {selectedStatus.length > 0 && (
                                                <>
                                                    <Dropdown.Divider />
                                                    <Dropdown.Item 
                                                        onClick={() => handleStatusFilterChange('')}
                                                        className="text-danger"
                                                    >
                                                        Clear All
                                                    </Dropdown.Item>
                                                </>
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                ),
                                cell: (row) => (
                                    <span className={`badge bg-${getStatusBadgeVariant(row)}`}>
                                        {getStatusText(row)}
                                    </span>
                                )
                            }] : []),
                            ...(columnVisibility.notes ? [{
                                id: 'notes',
                                header: 'Notes',
                                cell: (row) => highlightText(row.notes || '', searchTerm)
                            }] : []),
                            ...(columnVisibility.issue_description ? [{
                                id: 'issue_description',
                                header: 'Issue Description',
                                cell: (row) => highlightText(row.transfer_issue_description || '', searchTerm)
                            }] : []),
                            ...(columnVisibility.resolution_notes ? [{
                                id: 'resolution_notes',
                                header: 'Resolution Notes',
                                cell: (row) => highlightText(row.transfer_issue_resolution || '', searchTerm)
                            }] : [])
                        ]}
                        data={transfers}
                        initialColumnWidths={columnWidths}
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={setPageSize}
                    />
                </div>
            )}
        </Container>
    )
}

export default InventoryTransfersListScreen 